import type {CanvasChatMessage} from './canvas-types';

const runtimeProse = new Set([
 '', 'Creating your variations now.', 'Starting your image edit.',
 'Image generation submitted.', 'Making these ads now.',
 'Starting your ad campaign now.', 'Cloning from the winning-ads library now.',
 'Using your own ad creatives now.', 'Pulling competitor ads now.',
 'Applying your marked edits.', '[Agent completed — see action cards above]',
]);
const isRuntimeProse = (content:string) => runtimeProse.has(content.trim()) ||
 /^\d+ concepts? ready for .+\. Generating ads…$/.test(content.trim()) ||
 /^The live connection dropped, but your ads are still generating\b/.test(content.trim());
const count = (value:unknown):value is number => Number.isSafeInteger(value) && (value as number)>=0;

/** A terminal job receipt survives a dropped stream even when agent_done had
 * no text. Delivery counts come from the receipt, never QA totals/job status.
 * Replace only our own starter prose; a useful assistant explanation survives.
 */
function recoverTerminalGenerationSummary(message:CanvasChatMessage):CanvasChatMessage {
 if(message.role!=='assistant')return message;
 const jobs=message.playbookJobs??[];
 if(jobs.length!==1)return message;
 const job=jobs[0];
 if(job.assistantMessageId!==message.id || !['completed','failed'].includes(job.status))return message;
 const result=job.result as Record<string,unknown>|null|undefined;
 if(!result || result.deliveryUnverified===true)return message;
 const requested=result.requestedCount,delivered=result.deliveredCount,failed=result.failedCount;
 if(!count(requested)||!count(delivered)||!count(failed)||requested<1||delivered>requested||failed!==requested-delivered)return message;
 if(Array.isArray(result.generated)&&result.generated.length!==delivered)return message;
 const prefix=`${delivered} of ${requested} requested images delivered.`;
 const savedSummary=typeof result.message==='string'&&(job.kind==='conversation_tool'||result.message.startsWith(prefix))?result.message:undefined;
 let summary=savedSummary??
  `${prefix} ${failed?`${failed} image${failed===1?' was':'s were'} not delivered.`:'No images are missing.'}`;
 if(!savedSummary){
  const passed=result.qaPassedCount,review=result.qaFailedCount,unknown=result.qaUnavailableCount;
  if(count(passed)&&count(review)&&count(unknown)&&passed+review+unknown===delivered)
   summary+=` ${passed} passed quality review; ${review} need${review===1?'s':''} review; ${unknown} ${unknown===1?'is':'are'} not quality-verified.`;
 }
 const progress=message.playbookProgress;
 const ownsProgress=progress&&progress.workflowRunId!=null&&job.workflowRunIds?.includes(progress.workflowRunId);
 const replaceContent=(job.kind==='conversation_tool'||isRuntimeProse(message.content))&&message.content!==summary;
 if(!replaceContent&&(!ownsProgress||progress.completedSummary===summary))return message;
 return {...message,...(replaceContent?{content:summary}:{}),
  ...(ownsProgress?{playbookProgress:{...progress,completedSummary:summary}}:{})};
}

const syntheticRunId = (message:CanvasChatMessage):number|undefined => {
 const match=/^run-card-(\d+)$/.exec(message.id);
 if(message.role!=='assistant'||!match)return;
 const id=Number(match[1]);
 return Number.isSafeInteger(id)&&id>0&&(!message.playbookProgress?.workflowRunId||message.playbookProgress.workflowRunId===id)?id:undefined;
};
/** Replace a workflow-only recovery card with its actual assistant owner once
 * durable job or live progress identity arrives. Never dedupe by shared URLs,
 * labels, or proximity: distinct user requests must keep their own messages. */
export function reconcileRecoveredRunCards(messages:CanvasChatMessage[]):CanvasChatMessage[]{
 let next=messages;
 for(const fallback of messages){
  const runId=syntheticRunId(fallback);
  if(runId===undefined)continue;
  const real=next.filter(message=>message.role==='assistant'&&syntheticRunId(message)===undefined);
  const jobOwners=real.filter(message=>message.playbookJobs?.some(job=>job.assistantMessageId===message.id&&job.workflowRunIds?.includes(runId)));
  const candidates=jobOwners.length?jobOwners:real.filter(message=>message.playbookProgress?.workflowRunId===runId);
  if(candidates.length!==1)continue;
  const owner=candidates[0];
  const slots=[...(owner.imageSlots??[])];
  for(const slot of fallback.imageSlots??[]){
   if(!slot.resolved||slots.some(existing=>existing.resolved===slot.resolved))continue;
   const unresolved=slots.findIndex(existing=>!existing.resolved);
   if(unresolved>=0)slots[unresolved]={...slots[unresolved],resolved:slot.resolved,failed:false,errorMsg:undefined};
   else slots.push({...slot,index:Math.max(-1,...slots.map(existing=>existing.index))+1});
  }
  const merged={...owner,...(!owner.playbookProgress&&fallback.playbookProgress?{playbookProgress:{...fallback.playbookProgress,workflowRunId:runId}}:{}),...(slots.length?{imageSlots:slots}:{})};
  next=next.filter(message=>message!==fallback).map(message=>message===owner?merged:message);
 }
 const recovered=next.map(recoverTerminalGenerationSummary);
 return recovered.some((message,index)=>message!==next[index])?recovered:next;
}

/** Completed queue bookkeeping adds no second card when this message already
 * renders the completed workflow. Running/error states retain their controls. */
export function visiblePlaybookJobs(message:CanvasChatMessage){
 return (message.playbookJobs??[]).filter(job=>!(job.status==='completed'&&message.playbookProgress?.isDone&&message.playbookProgress.workflowRunId&&job.workflowRunIds?.includes(message.playbookProgress.workflowRunId)));
}
